import { Component, inject, input, output } from '@angular/core';
import { LucideAngularModule } from 'lucide-angular';
import { I18nService } from '../../../../core';
import type { NotificationItem } from '../../models/notifications.models';
import { formatApiUtcTimestamp } from '../../../../shared/utils';

@Component({
  selector: 'app-notification-item',
  standalone: true,
  imports: [LucideAngularModule],
  template: `
    <button type="button" class="item" [class.unread]="!item().isRead" (click)="open.emit(item())">
      <div class="dot-wrap">
        @if (!item().isRead) {
          <span class="unread-dot" aria-hidden="true"></span>
        }
      </div>
      <div class="content">
        <span class="type-label">{{ typeLabel(item().type) }}</span>
        <strong>{{ item().title }}</strong>
        <p [class.message-preline]="messageIsMultiline()">{{ item().message }}</p>
        <small>{{ formatDate(item().occurredAt) }}</small>
      </div>
    </button>
  `,
  styles: [
    `
      .item {
        width: 100%;
        border: 1px solid var(--border);
        border-radius: 10px;
        background: var(--bg-surface);
        display: grid;
        grid-template-columns: 14px 1fr;
        gap: 8px;
        padding: 10px;
        text-align: left;
      }

      .item.unread {
        border-color: color-mix(in srgb, #2563eb 34%, var(--border));
        background: color-mix(in srgb, #2563eb 4%, var(--bg-surface));
      }

      .dot-wrap {
        display: flex;
        align-items: start;
        justify-content: center;
        padding-top: 4px;
      }

      .unread-dot {
        width: 8px;
        height: 8px;
        border-radius: 999px;
        background: #2563eb;
      }

      .content {
        display: grid;
        gap: 3px;
        min-width: 0;
      }

      .type-label {
        font-size: 10px;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.04em;
        color: var(--faint);
      }

      strong {
        color: var(--text);
        font-size: 12px;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      p {
        margin: 0;
        color: var(--muted);
        font-size: 12px;
        line-height: 1.4;
        display: -webkit-box;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
        overflow: hidden;
      }

      p.message-preline {
        display: block;
        white-space: pre-line;
        -webkit-line-clamp: unset;
        -webkit-box-orient: unset;
        overflow-y: auto;
        max-height: 5.25rem;
      }

      small {
        color: var(--faint);
        font-size: 11px;
      }
    `,
  ],
})
export class NotificationItemComponent {
  private readonly i18n = inject(I18nService);

  readonly item = input.required<NotificationItem>();
  readonly open = output<NotificationItem>();

  formatDate(value: string): string {
    return formatApiUtcTimestamp(value, {
      locale: this.i18n.locale(),
      intl: {
        dateStyle: 'short',
        timeStyle: 'short',
      },
      fallback: '-',
    });
  }

  typeLabel(type: string): string {
    const key = `notifications.typeLabel.${type.trim().toLowerCase()}`;
    return this.i18n.t(key, undefined, type.trim() || '—');
  }

  messageIsMultiline(): boolean {
    return (this.item().message ?? '').includes('\n');
  }
}
